(() => {
  const STORAGE_KEY = 'nexvey-hot-picks-compare-v1';
  const CARD_BASE_WIDTH = 437;

  const updateCardScale = (card, measuredWidth = card?.clientWidth) => {
    if (!card || !measuredWidth) return;
    const scale = Math.min(1, Math.max(0.1, measuredWidth / CARD_BASE_WIDTH));
    card.style.setProperty('--nhp-card-scale', scale.toFixed(5));
  };

  const cardScaleObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => updateCardScale(entry.target, entry.contentRect.width));
    })
    : null;

  const observeCardScaling = (root) => {
    root.querySelectorAll('[data-hot-picks-card]').forEach((card) => {
      updateCardScale(card);
      cardScaleObserver?.observe(card);
    });
  };

  const readSelection = () => {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && (item.productId || item.id)) : [];
    } catch (error) {
      return [];
    }
  };

  const writeSelection = (items) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // Comparison still works on the current page if storage is unavailable.
    }
  };

  const formatTemplate = (template, values) => Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    String(template || '')
  );

  const parseBike = (card) => {
    const source = card?.querySelector('[data-hot-picks-bike-json]');
    if (!source) return null;
    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      return null;
    }
  };

  const selectionKey = (item) => String(item?.productId || item?.id || '').trim();

  const makeChip = (bike, root) => {
    const chip = document.createElement('span');
    chip.className = 'nhp-compare-chip';
    chip.dataset.compareChip = selectionKey(bike);

    if (bike.image) {
      const image = document.createElement('img');
      image.src = bike.image;
      image.alt = '';
      image.loading = 'lazy';
      chip.appendChild(image);
    }

    const label = document.createElement('span');
    label.textContent = bike.title || bike.id || bike.productId;
    chip.appendChild(label);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.compareRemove = selectionKey(bike);
    remove.setAttribute('aria-label', `Remove ${bike.title || bike.id || bike.productId}`);
    remove.textContent = '×';
    chip.appendChild(remove);

    return chip;
  };

  const renderComparisonState = () => {
    const items = readSelection();
    const ids = new Set(items.map(selectionKey).filter(Boolean));

    document.querySelectorAll('[data-nexvey-hot-picks]').forEach((root) => {
      root.querySelectorAll('[data-hot-picks-card]').forEach((card) => {
        const isSelected = ids.has(String(card.dataset.bikeProductId || card.dataset.bikeId))
          || ids.has(String(card.dataset.bikeId || ''));
        const toggle = card.querySelector('[data-compare-toggle]');
        if (toggle) toggle.setAttribute('aria-pressed', String(isSelected));
        card.classList.toggle('is-compare-selected', isSelected);
      });

      const bar = root.querySelector('[data-compare-bar]');
      const count = root.querySelector('[data-compare-count]');
      const list = root.querySelector('[data-compare-items]');
      if (!bar || !list) return;

      bar.hidden = items.length === 0;
      if (count) count.textContent = String(items.length);
      list.replaceChildren(...items.map((item) => makeChip(item, root)));
    });
  };

  const toggleComparison = (card) => {
    const bike = parseBike(card);
    const key = selectionKey(bike);
    if (!key) return;

    const items = readSelection();
    const index = items.findIndex((item) => selectionKey(item) === key || String(item.id || '') === String(bike.id || ''));
    if (index >= 0) items.splice(index, 1);
    else items.push(bike);
    writeSelection(items);
    renderComparisonState();
  };

  const selectCardColor = (button) => {
    const card = button.closest('[data-hot-picks-card]');
    const image = card?.querySelector('.nhp-card__image');
    const imageUrl = button.dataset.colorImage;
    if (!card || !image || !imageUrl) return;

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = imageUrl;

    const title = parseBike(card)?.title || '';
    const colorLabel = button.dataset.colorLabel || '';
    image.alt = [title, colorLabel].filter(Boolean).join(' - ');

    card.querySelectorAll('[data-card-color]').forEach((item) => {
      const isSelected = item === button;
      item.classList.toggle('is-active', isSelected);
      item.setAttribute('aria-pressed', String(isSelected));
    });
  };

  const removeComparison = (id) => {
    const items = readSelection().filter((item) => selectionKey(item) !== String(id));
    writeSelection(items);
    renderComparisonState();
  };

  const nearestCardToCenter = (rail) => {
    const cards = [...rail.querySelectorAll('[data-hot-picks-card]')];
    if (!cards.length) return null;
    const railBox = rail.getBoundingClientRect();
    const center = railBox.left + railBox.width / 2;
    return cards.reduce((nearest, card) => {
      const box = card.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - center);
      return !nearest || distance < nearest.distance ? { card, distance } : nearest;
    }, null)?.card;
  };

  const updateCenteredCard = (rail) => {
    const centered = nearestCardToCenter(rail);
    rail.querySelectorAll('[data-hot-picks-card]').forEach((card) => {
      const isCentered = card === centered;
      card.classList.toggle('is-center-card', isCentered);
      if (isCentered) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
    });
  };

  const updateNav = (root) => {
    const rail = root.querySelector('[data-hot-picks-rail]');
    const previous = root.querySelector('[data-rail-prev]');
    const next = root.querySelector('[data-rail-next]');
    if (!rail) return;
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    if (previous) previous.disabled = rail.scrollLeft <= 2;
    if (next) next.disabled = rail.scrollLeft >= maxScroll - 2;
  };

  const scrollRail = (root, direction) => {
    const rail = root.querySelector('[data-hot-picks-rail]');
    const card = rail?.querySelector('[data-hot-picks-card]');
    if (!rail || !card) return;
    const styles = window.getComputedStyle(rail);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    rail.scrollBy({
      left: direction * (card.getBoundingClientRect().width + gap),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };

  const scrollCardHorizontally = (rail, card, align = 'start') => {
    if (!rail || !card) return;
    const railBox = rail.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const styles = window.getComputedStyle(rail);
    const safeInset = Math.max(4, Number.parseFloat(styles.paddingLeft || '0') || 0);
    const currentCardLeft = rail.scrollLeft + cardBox.left - railBox.left;
    const targetLeft = align === 'center'
      ? currentCardLeft - (rail.clientWidth - cardBox.width) / 2
      : currentCardLeft - safeInset;
    rail.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };

  const applyAgeRecommendation = (root, button, options = {}) => {
    const key = String(button.dataset.ageFilter || 'all').toLowerCase();
    const label = button.dataset.ageLabel || button.textContent.trim();
    const cards = [...root.querySelectorAll('[data-hot-picks-card]')];
    const rail = root.querySelector('[data-hot-picks-rail]');
    const status = root.querySelector('[data-hot-picks-status]');
    const isAll = key === 'all';
    const shouldScroll = options.scroll !== false;
    let recommendedCount = 0;
    let firstRecommendedCard = null;

    root.querySelectorAll('[data-age-filter]').forEach((item) => {
      const isSelected = item === button;
      item.classList.toggle('is-active', isSelected);
      item.setAttribute('aria-pressed', String(isSelected));
    });

    cards.forEach((card) => {
      const keys = String(card.dataset.ageKeys || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
      const isRecommended = !isAll && keys.includes(key);
      if (isRecommended) {
        recommendedCount += 1;
        if (!firstRecommendedCard) firstRecommendedCard = card;
      }
      card.classList.toggle('is-recommended', isRecommended);
      card.style.removeProperty('--nhp-order');
      const chip = card.querySelector('[data-recommended-chip]');
      if (chip) {
        chip.hidden = !isRecommended;
        chip.textContent = formatTemplate(root.dataset.recommendedLabel, { age: label });
      }
    });

    if (status) {
      status.textContent = isAll
        ? formatTemplate(root.dataset.statusAll, { count: cards.length, total: cards.length, age: label })
        : formatTemplate(root.dataset.statusRecommended, { count: recommendedCount, total: cards.length, age: label });
    }

    requestAnimationFrame(() => {
      if (!rail) return;
      if (shouldScroll) {
        const targetCard = firstRecommendedCard || (isAll ? cards[0] : null);
        if (targetCard) {
          scrollCardHorizontally(
            rail,
            targetCard,
            window.matchMedia('(max-width: 749px)').matches ? 'center' : 'start'
          );
        }
      }
      updateCenteredCard(rail);
      updateNav(root);
    });
  };

  const enableRailDragging = (rail) => {
    if (!rail || rail.dataset.dragInitialized === 'true') return;
    rail.dataset.dragInitialized = 'true';

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let hasMoved = false;
    let suppressClick = false;
    let suppressTimer = 0;

    const finishDrag = (event) => {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      suppressClick = hasMoved;
      window.clearTimeout(suppressTimer);
      if (suppressClick) {
        suppressTimer = window.setTimeout(() => {
          suppressClick = false;
        }, 180);
      }
      if (rail.hasPointerCapture?.(pointerId)) rail.releasePointerCapture(pointerId);
      pointerId = null;
      hasMoved = false;
      rail.classList.remove('is-dragging');
    };

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea')) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = rail.scrollLeft;
      hasMoved = false;
      rail.setPointerCapture?.(pointerId);
    });

    rail.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (!hasMoved && Math.abs(distance) < 5) return;
      hasMoved = true;
      rail.classList.add('is-dragging');
      rail.scrollLeft = startScrollLeft - distance;
      event.preventDefault();
    });

    rail.addEventListener('pointerup', finishDrag);
    rail.addEventListener('pointercancel', finishDrag);
    rail.addEventListener('lostpointercapture', finishDrag);
    rail.addEventListener('dragstart', (event) => event.preventDefault());
    rail.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
      window.clearTimeout(suppressTimer);
    }, true);
  };

  const initRoot = (root) => {
    if (!root || root.dataset.hotPicksInitialized === 'true') return;
    root.dataset.hotPicksInitialized = 'true';

    const rail = root.querySelector('[data-hot-picks-rail]');
    let scrollFrame = 0;
    observeCardScaling(root);
    enableRailDragging(rail);

    root.addEventListener('click', (event) => {
      const compareModelsLink = event.target.closest('[data-compare-models-link]');
      if (compareModelsLink) {
        event.preventDefault();
        const productIds = readSelection()
          .map((item) => String(item.productId || '').trim())
          .filter(Boolean);
        const target = new URL(root.dataset.compareUrl || compareModelsLink.href, window.location.origin);

        if (productIds.length) {
          target.searchParams.set('product_ids', [...new Set(productIds)].join(','));
          target.searchParams.delete('models');
          target.searchParams.set('source', 'hot-picks');
        } else {
          const handles = readSelection()
            .map((item) => String(item.id || '').trim())
            .filter(Boolean);
          if (handles.length) target.searchParams.set('models', [...new Set(handles)].join(','));
          else target.searchParams.delete('models');
          target.searchParams.delete('product_ids');
          target.searchParams.delete('source');
        }
        window.location.assign(target.toString());
        return;
      }

      const colorButton = event.target.closest('[data-card-color]');
      if (colorButton) {
        selectCardColor(colorButton);
        return;
      }

      const ageButton = event.target.closest('[data-age-filter]');
      if (ageButton) {
        applyAgeRecommendation(root, ageButton);
        return;
      }

      const compareToggle = event.target.closest('[data-compare-toggle]');
      if (compareToggle) {
        toggleComparison(compareToggle.closest('[data-hot-picks-card]'));
        return;
      }

      const remove = event.target.closest('[data-compare-remove]');
      if (remove) {
        removeComparison(remove.dataset.compareRemove);
        return;
      }

      if (event.target.closest('[data-compare-clear]')) {
        writeSelection([]);
        renderComparisonState();
        return;
      }

      if (event.target.closest('[data-rail-prev]')) scrollRail(root, -1);
      if (event.target.closest('[data-rail-next]')) scrollRail(root, 1);
    });

    rail?.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        updateCenteredCard(rail);
        updateNav(root);
      });
    }, { passive: true });

    const selectedAge = root.querySelector('[data-age-filter][aria-pressed="true"]')
      || root.querySelector('[data-age-filter]');
    const shouldInitialScroll = String(selectedAge?.dataset.ageFilter || 'all').toLowerCase() !== 'all';
    if (selectedAge) applyAgeRecommendation(root, selectedAge, { scroll: shouldInitialScroll });

    requestAnimationFrame(() => {
      if (rail && window.matchMedia('(max-width: 749px)').matches && !shouldInitialScroll) {
        rail.scrollLeft = 0;
      }
      if (rail) updateCenteredCard(rail);
      updateNav(root);
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-nexvey-hot-picks]')) initRoot(scope);
    scope.querySelectorAll?.('[data-nexvey-hot-picks]').forEach(initRoot);
    renderComparisonState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-hot-picks-card]').forEach((card) => cardScaleObserver?.unobserve(card));
  });
  window.addEventListener('pageshow', renderComparisonState);
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-nexvey-hot-picks]').forEach((root) => {
      root.querySelectorAll('[data-hot-picks-card]').forEach((card) => updateCardScale(card));
      const rail = root.querySelector('[data-hot-picks-rail]');
      if (rail) updateCenteredCard(rail);
      updateNav(root);
    });
  });
})();
